import { ReportQuery } from '../../../core/services/report-query';
import { RecordListColumn, RecordListState } from './record-list.types';

export const DEFAULT_RECORD_LIST_STATE: RecordListState = {
  search: '',
  filters: {},
  page: 1,
  perPage: 25,
};

/** URL/query serialization only; persistence and owner services remain page-specific. */
export function recordListRouteState(params: { get(key: string): string | null }): RecordListState {
  let filters = {};
  try { const parsed = JSON.parse(params.get('filters') ?? '{}'); if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) filters = parsed; }
  catch { console.warn('[RecordList] Ignoring malformed URL filters.'); }
  return normalizeRecordListState({search:params.get('q') ?? '', filters,
    page:Number(params.get('page') ?? 1), perPage:Number(params.get('perPage') ?? 25),
    sort:{key:params.get('sort') ?? 'name', direction:params.get('direction') === 'desc' ? 'desc' : 'asc'}});
}

export function recordListRouteParams(state: RecordListState): Record<string, string | number | null> {
  return {q:state.search || null, filters:Object.keys(state.filters).length ? JSON.stringify(state.filters) : null,
    page:state.page > 1 ? state.page : null, perPage:state.perPage !== 25 ? state.perPage : null,
    sort:state.sort?.key !== 'name' ? state.sort?.key ?? null : null, direction:state.sort?.direction === 'desc' ? 'desc' : null};
}

export function recordListOwnerParams(state: RecordListState): URLSearchParams {
  const filters = Object.entries(state.filters).map(([key, filter]) => ({key, operator:filter.operator, value:filter.value}));
  return new URLSearchParams({page:String(state.page), limit:String(state.perPage), search:state.search,
    sortColumn:state.sort?.key ?? 'name', sortDirection:state.sort?.direction ?? 'asc', filters:JSON.stringify(filters)});
}

export function normalizeRecordListState(state?: Partial<RecordListState> | null): RecordListState {
  const page = Number(state?.page);
  const perPage = Number(state?.perPage);
  return {
    search: String(state?.search ?? '').trim(),
    filters: state?.filters && typeof state.filters === 'object' ? clone(state.filters) : {},
    page: Number.isInteger(page) && page > 0 ? page : 1,
    perPage: Number.isInteger(perPage) && perPage > 0 && perPage <= 100 ? perPage : 25,
    ...(state?.sort?.key
      ? {
          sort: {
            key: state.sort.key,
            direction: state.sort.direction === 'desc' ? 'desc' : 'asc',
          },
        }
      : {}),
  };
}

export function buildRecordListQuery(
  baseQuery: ReportQuery,
  columns: RecordListColumn[],
  state: RecordListState,
): ReportQuery {
  const query = clone(baseQuery);
  const filters: Array<Record<string, unknown>> = [];
  const search = state.search.trim();

  if (search) {
    const searchConditions = columns
      .filter((column) => column.filter?.searchable)
      .map((column) => ({
        type: 'condition',
        field: column.filter?.field ?? column.field,
        operator: 'contains',
        value: search,
      }));
    if (searchConditions.length) {
      filters.push({
        type: 'group',
        operator: 'or',
        children: searchConditions,
      });
    }
  }

  for (const column of columns) {
    const filterState = state.filters[column.key];
    if (!filterState || column.filter?.filterable === false || !column.filter) {
      continue;
    }
    const values = Array.isArray(filterState.value) ? filterState.value : [filterState.value];
    if (!values.length || values.every((value) => String(value).trim() === '')) {
      continue;
    }
    const converted = values.map((value) => coerceFilterValue(value, column.filter?.valueType));
    const isMultiEqual = filterState.operator === 'eq' && converted.length > 1;
    filters.push({
      type: 'condition',
      field: column.filter.field ?? column.field,
      operator: isMultiEqual ? 'in' : filterState.operator,
      value: filterState.operator === 'between' || isMultiEqual ? converted : converted[0],
    });
  }

  if (query.where) {
    filters.unshift(query.where);
  }

  query.where =
    filters.length === 0
      ? undefined
      : filters.length === 1
        ? filters[0]
        : { type: 'group', operator: 'and', children: filters };

  const sortColumn = state.sort
    ? columns.find((column) => column.key === state.sort?.key && column.sortable)
    : undefined;
  query.order_by =
    sortColumn && state.sort
      ? [{ ref: sortColumn.key, direction: state.sort.direction }]
      : query.order_by;
  query.limit = state.perPage;
  query.offset = (state.page - 1) * state.perPage;
  query.include_total = true;
  return query;
}

export function interpolateRecordTemplate(
  template: string,
  row: Record<string, unknown>,
  context: string,
): string {
  const token = /{{\s*query_result\.([A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*)\s*}}/g;
  let invalid = false;
  const rendered = template.replace(token, (_match, path: string) => {
    const resolved = resolveRecordPath(row, path);
    if (!resolved.found) {
      console.error(
        "[RecordList] Missing query result field '" + path + "' in " + context + '.',
        row,
      );
      invalid = true;
      return '';
    }
    return resolved.value == null ? '' : String(resolved.value);
  });
  if (rendered.includes('{{') || rendered.includes('}}')) {
    console.error(
      '[RecordList] Invalid template in ' +
        context +
        '. Only {{query_result.field}} tokens are supported.',
      template,
    );
    invalid = true;
  }
  return invalid ? rendered.replace(/{{|}}/g, '') : rendered;
}

export function resolveRecordPath(
  row: Record<string, unknown>,
  path: string,
): { found: boolean; value: unknown } {
  let current: unknown = row;
  for (const segment of path.split('.')) {
    if (
      !current ||
      typeof current !== 'object' ||
      !Object.prototype.hasOwnProperty.call(current, segment)
    ) {
      return { found: false, value: undefined };
    }
    current = (current as Record<string, unknown>)[segment];
  }
  return { found: true, value: current };
}

export function validateRecordListColumns(columns: RecordListColumn[]): string | null {
  if (!columns.length) {
    return 'The record list requires at least one column.';
  }
  const primaryCount = columns.filter((column) => column.primary).length;
  if (primaryCount !== 1) {
    return 'The record list requires exactly one primary column; received ' + primaryCount + '.';
  }
  const keys = new Set<string>();
  for (const column of columns) {
    if (column.openRecord && column.link) {
      return "Record-list column '" + column.key + "' cannot combine a route link with a modal opener.";
    }
    if (!column.key || !column.field) {
      return 'Every record-list column requires a key and an RQL field.';
    }
    if (keys.has(column.key)) {
      return "Record-list column key '" + column.key + "' is duplicated.";
    }
    keys.add(column.key);
  }
  return null;
}

export function filterStateSignature(state: RecordListState): string {
  return JSON.stringify(normalizeRecordListState(state));
}

function coerceFilterValue(
  value: string | number | boolean,
  valueType?: string,
): string | number | boolean {
  if (valueType === 'boolean') {
    return value === true || value === 'true';
  }
  if (
    valueType === 'integer' ||
    valueType === 'number' ||
    valueType === 'currency' ||
    valueType === 'percentage'
  ) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : value;
  }
  if (valueType === 'datetime' && typeof value === 'string' && value && !value.endsWith('Z')) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
  }
  return value;
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}
